import { isAxiosError } from 'axios'
import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'

export type SocialPlatform = 'telegram' | 'vk'

export const SOCIAL_PUBLICATION_STATUSES = ['awaiting_approval', 'approved', 'finished', 'cancelled'] as const
export type SocialPublicationStatus = (typeof SOCIAL_PUBLICATION_STATUSES)[number]

export const SOCIAL_ITEM_STATUSES = [
  'pending',
  'sending',
  'sent',
  'failed',
  'needs_human',
  'needs_credentials',
  'needs_captcha',
] as const
/**
 * needs_human — исход отправки неизвестен, повтор только решением человека;
 * needs_credentials — платформа отклонила токен; needs_captcha — VK запросил
 * капчу, запись не создана.
 */
export type SocialItemStatus = (typeof SOCIAL_ITEM_STATUSES)[number]

/** Цель публикаций: канал Telegram или сообщество VK. Токена в ответе нет ни в каком виде. */
export interface SocialTarget {
  id: number
  key: string
  platform: SocialPlatform
  chat_id: string | null
  group_id: number | null
  /** Название канала или сообщества из последней успешной проверки */
  title: string | null
  title_checked_at: string | null
  enabled: boolean
  next_allowed_at: string | null
  has_credentials: boolean
  /** Звёздочки и последние 4 символа токена */
  credentials_mask: string | null
  credentials_updated_at: string | null
  credentials_updated_by: number | null
  last_check_at: string | null
  last_check_ok: boolean | null
  /** Только код ошибки последней проверки, например telegram.401 */
  last_check_error: string | null
  created_at: string | null
  updated_at: string | null
}

export interface SocialItemTarget {
  id: number
  key: string
  platform: SocialPlatform
  title: string | null
  enabled: boolean
}

/** Шаг публикации — отправка в одну цель */
export interface SocialPublicationItem {
  id: number
  social_publication_id: number
  social_target_id: number
  /** В vk_posts_to_delete ответа отмены цель не подгружается — поля нет */
  target?: SocialItemTarget | null
  status: SocialItemStatus
  available_at: string | null
  /** message_id Telegram или post_id VK */
  external_id: string | null
  url: string | null
  error_code: string | null
  error_details: string | null
  retryable: boolean
  captcha_sid: string | null
  captcha_img: string | null
  attempts: number
  claimed_at: string | null
  sent_at: string | null
  marked_sent_by: number | null
  created_at: string | null
  updated_at: string | null
}

export interface SocialPublicationNews {
  id: number
  title: string
  slug: string
  is_published: boolean
  published_at: string | null
  /** Видна ли новость на сайте сейчас — без этого одобрение даёт 422 */
  is_public: boolean
  /** Публичный url новости на сайте */
  url: string
}

/** Inline-кнопка под постом Telegram: надпись на языке канала и адрес новости */
export interface SocialTelegramButton {
  text: string
  url: string
}

export interface SocialPublication {
  id: number
  idempotency_key: string
  status: SocialPublicationStatus
  news_id: number
  news?: SocialPublicationNews | null
  /** sha256 содержимого, которое видел человек, — его отправляет кнопка «Опубликовать» */
  content_hash: string
  text_telegram: string | null
  text_vk: string | null
  cover_path: string | null
  cover_sha256: string | null
  cover_url: string | null
  /** Своя картинка поста; null — картинкой поста служит обложка новости */
  image_path: string | null
  image_sha256: string | null
  image_url: string | null
  /** Кнопка под постом Telegram, какой её поставит отправка; null — без кнопки. Нет, если новость не подгружена */
  telegram_button?: SocialTelegramButton | null
  /** text_vk содержит текущий адрес новости; false — шаг VK не отправится. Нет, если новость не подгружена */
  vk_news_url_in_text?: boolean
  publish_at: string | null
  created_by: number | null
  approved_by: number | null
  approved_at: string | null
  cancelled_by: number | null
  cancelled_at: string | null
  items: SocialPublicationItem[]
  created_at: string | null
  updated_at: string | null
}

export interface SocialPaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

/** Ответ действия: локализованное сообщение сервера и объект после изменения */
export interface SocialActionResponse<T> {
  message: string
  data: T
}

export interface SocialCancelResponse extends SocialActionResponse<SocialPublication> {
  /** Уже созданные отложенные записи VK: остаются в VK, удалять вручную */
  vk_posts_to_delete: SocialPublicationItem[]
}

export interface SocialPublicationsParams {
  status?: SocialPublicationStatus
  page?: number
  per_page?: number
}

export interface CreateSocialTargetInput {
  platform: SocialPlatform
  key: string
  chat_id?: string
  group_id?: number
}

export interface UpdateSocialTargetInput {
  enabled?: boolean
  chat_id?: string
  group_id?: number
}

export interface SocialImageFile {
  blob: Blob
  filename: string
}

const IMAGE_EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
}

/** HTTP-статус ответа API; null — ответа не было */
export function socialErrorStatus(error: unknown): number | null {
  return isAxiosError(error) ? (error.response?.status ?? null) : null
}

/** Код 409 approve: содержимое разошлось с тем, что было при создании публикации */
export const SOCIAL_CONTENT_CHANGED_ERROR = 'social_content_changed'

/** Код ошибки из поля error тела ответа, например social_content_changed; null — кода нет */
export function socialErrorCode(error: unknown): string | null {
  if (!isAxiosError(error)) {
    return null
  }

  const code = (error.response?.data as { error?: unknown } | undefined)?.error
  return typeof code === 'string' ? code : null
}

/** Первое сообщение валидации по каждому полю ответа 422 */
export function socialFieldErrors(error: unknown): Record<string, string> {
  if (!isAxiosError(error) || error.response?.status !== 422) {
    return {}
  }

  const errors = (error.response.data as { errors?: unknown } | undefined)?.errors
  if (errors === null || typeof errors !== 'object') {
    return {}
  }

  const result: Record<string, string> = {}
  for (const [field, messages] of Object.entries(errors as Record<string, unknown>)) {
    if (Array.isArray(messages) && typeof messages[0] === 'string') {
      result[field] = messages[0]
    }
  }

  return result
}

/**
 * Ошибка запроса с responseType blob приходит телом-Blob, и getErrorMessage
 * не нашёл бы в нём message сервера. JSON-тело разворачивается обратно.
 */
async function withJsonErrorBody(error: unknown): Promise<unknown> {
  if (isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      error.response.data = JSON.parse(await error.response.data.text())
    } catch {
      // Тело не JSON: сообщение возьмётся из локализованного fallback
    }
  }

  return error
}

export const socialApi = {
  // --- Цели ---

  getTargets: async (): Promise<SocialTarget[]> => {
    const response = await apiClient.get<{ data: SocialTarget[] }>(API_ENDPOINTS.ADMIN.SOCIAL.TARGETS)
    return response.data.data
  },

  createTarget: async (body: CreateSocialTargetInput): Promise<SocialActionResponse<SocialTarget>> => {
    const response = await apiClient.post<SocialActionResponse<SocialTarget>>(
      API_ENDPOINTS.ADMIN.SOCIAL.TARGETS,
      body,
    )
    return response.data
  },

  updateTarget: async (id: number, body: UpdateSocialTargetInput): Promise<SocialActionResponse<SocialTarget>> => {
    const response = await apiClient.patch<SocialActionResponse<SocialTarget>>(
      API_ENDPOINTS.ADMIN.SOCIAL.TARGET(id),
      body,
    )
    return response.data
  },

  /** Токен уходит только телом PUT: не в URL, не в стор и не в логи. В ответе — маска. */
  storeCredentials: async (id: number, token: string): Promise<SocialActionResponse<SocialTarget>> => {
    const response = await apiClient.put<SocialActionResponse<SocialTarget>>(
      API_ENDPOINTS.ADMIN.SOCIAL.TARGET_CREDENTIALS(id),
      { token },
    )
    return response.data
  },

  revokeCredentials: async (id: number): Promise<SocialActionResponse<SocialTarget>> => {
    const response = await apiClient.delete<SocialActionResponse<SocialTarget>>(
      API_ENDPOINTS.ADMIN.SOCIAL.TARGET_CREDENTIALS(id),
    )
    return response.data
  },

  /** Итог проверки — всегда 200: успех в data.last_check_ok, текст в message */
  testTarget: async (id: number): Promise<SocialActionResponse<SocialTarget>> => {
    const response = await apiClient.post<SocialActionResponse<SocialTarget>>(
      API_ENDPOINTS.ADMIN.SOCIAL.TARGET_TEST(id),
    )
    return response.data
  },

  // --- Публикации ---

  getPublications: async (
    params: SocialPublicationsParams,
  ): Promise<{ data: SocialPublication[]; meta: SocialPaginationMeta }> => {
    const response = await apiClient.get<{ data: SocialPublication[]; meta: SocialPaginationMeta }>(
      API_ENDPOINTS.ADMIN.SOCIAL.PUBLICATIONS,
      { params },
    )
    return { data: response.data.data, meta: response.data.meta }
  },

  getPublication: async (id: number): Promise<SocialPublication> => {
    const response = await apiClient.get<{ data: SocialPublication }>(API_ENDPOINTS.ADMIN.SOCIAL.PUBLICATION(id))
    return response.data.data
  },

  /** 409 — содержимое или статус изменились после того, как человек открыл карточку */
  approve: async (id: number, contentHash: string): Promise<SocialActionResponse<SocialPublication>> => {
    const response = await apiClient.post<SocialActionResponse<SocialPublication>>(
      API_ENDPOINTS.ADMIN.SOCIAL.PUBLICATION_APPROVE(id),
      { content_hash: contentHash },
    )
    return response.data
  },

  cancel: async (id: number): Promise<SocialCancelResponse> => {
    const response = await apiClient.post<SocialCancelResponse>(API_ENDPOINTS.ADMIN.SOCIAL.PUBLICATION_CANCEL(id))
    return response.data
  },

  /**
   * Картинка поста для ручной публикации: своя картинка публикации, иначе
   * обложка на момент создания — эндпоинт выбирает сам.
   */
  downloadImage: async (id: number, ownImage: boolean): Promise<SocialImageFile> => {
    try {
      const response = await apiClient.get<Blob>(API_ENDPOINTS.ADMIN.SOCIAL.PUBLICATION_COVER(id), {
        responseType: 'blob',
      })
      const extension = IMAGE_EXTENSIONS[response.data.type]

      return {
        blob: response.data,
        filename: `social-publication-${id}-${ownImage ? 'image' : 'cover'}${extension ? `.${extension}` : ''}`,
      }
    } catch (error) {
      throw await withJsonErrorBody(error)
    }
  },

  // --- Шаги ---

  retryItem: async (id: number): Promise<SocialActionResponse<SocialPublication>> => {
    const response = await apiClient.post<SocialActionResponse<SocialPublication>>(
      API_ENDPOINTS.ADMIN.SOCIAL.ITEM_RETRY(id),
    )
    return response.data
  },

  /** Без url прежняя ссылка шага сохраняется */
  markItemSent: async (id: number, url: string | null): Promise<SocialActionResponse<SocialPublication>> => {
    const response = await apiClient.post<SocialActionResponse<SocialPublication>>(
      API_ENDPOINTS.ADMIN.SOCIAL.ITEM_MARK_SENT(id),
      url === null ? {} : { url },
    )
    return response.data
  },
}
